// ============================================================
// Mouvements des points boutique (Statistiques > Points Boutique) : fonctions pures, sans I/O ni store
// ============================================================
// Données du satellite (route /api/stats/storepoints/flows, 1.0.34) : historique de StorePointApi classé d'après le
// motif que chaque mod écrit. Créés : courrier, box, pass, récompenses des factions et des pays, recrédits, ajouts sans
// motif. Dépensés : boutique, market, emplacements du market, échanges, retraits sans motif. Une part des dépenses
// passe à d'autres joueurs (ventes du market, échanges), le reste sort du jeu.

/** Version du satellite qui sert le bloc : la page le dit face à un satellite plus ancien. */
export const FLOWS_SATELLITE_VERSION = '1.0.34';

export type PointsType = 'seasonal' | 'classic' | 'all';

export const POINTS_TYPES: { id: PointsType; label: string }[] = [
  { id: 'seasonal', label: 'Saisonniers' },
  { id: 'classic', label: 'Classiques' },
  { id: 'all', label: 'Tous les points' },
];

/** « saisonniers », « classiques » ou rien, pour finir une phrase (« Points saisonniers créés »). */
export function typeWord(type: PointsType): string {
  return type === 'seasonal' ? ' saisonniers' : type === 'classic' ? ' classiques' : '';
}

/** Sources des points créés, dans l'ordre fixe des couleurs (celui du satellite). */
export const SOURCE_ORDER = ['mailbox', 'box', 'gamepass', 'faction_weekly', 'faction_season', 'recredit', 'added', 'other_in'] as const;

/** Destinations des points dépensés, dans l'ordre fixe des couleurs (celui du satellite). */
export const DESTINATION_ORDER = ['shop', 'market', 'market_slot', 'trade', 'subtracted', 'other_out'] as const;

const SOURCE_LABELS: Record<string, string> = {
  mailbox: 'Courrier',
  box: 'Box',
  gamepass: 'Pass',
  faction_weekly: 'Factions (semaine)',
  faction_season: 'Factions (fin de saison)',
  recredit: 'Recrédit',
  added: 'Ajout sans motif',
  other_in: 'Autres crédits',
};

const SOURCE_HINTS: Record<string, string> = {
  mailbox: "pièce jointe d'un courrier, comptée quand le joueur l'ouvre : achats de la boutique web et récompenses envoyées par courrier, sans distinction dans l'historique.",
  box: "lots en points des box et lootbox (PremiumMod).",
  gamepass: "récompenses des paliers du pass.",
  faction_weekly: "récompense hebdomadaire des chefs de faction (des pays sur NationsGlory), selon leur ligue.",
  faction_season: "récompense de fin de saison des chefs de faction selon leur rang.",
  recredit: "points perdus rendus par l'outil de recrédit du courrier.",
  added: "ajout fait sans donner de motif : appel direct à StorePointApi (outil du staff, script).",
  other_in: "crédit dont le motif n'est pas encore classé : voir les motifs pas encore classés.",
};

const DESTINATION_LABELS: Record<string, string> = {
  shop: 'Boutique',
  market: 'Market (achats)',
  market_slot: 'Market (emplacements)',
  trade: 'Échanges',
  subtracted: 'Retrait sans motif (clear)',
  other_out: 'Autres retraits',
};

const DESTINATION_HINTS: Record<string, string> = {
  shop: "achats de la boutique en jeu (RecubeBoutiqueMod), remboursements déduits.",
  market: "ce que paient les acheteurs ; la part reçue par les vendeurs passe à d'autres joueurs, le reste sort du jeu.",
  market_slot: "achat d'un emplacement de vente au market.",
  trade: "ce que les joueurs envoient ; ce que reçoit l'autre joueur passe à lui, la taxe sort du jeu.",
  subtracted: "retrait fait sans donner de motif : appel direct à StorePointApi (outil du staff, script).",
  other_out: "retrait dont le motif n'est pas encore classé : voir les motifs pas encore classés.",
};

export function sourceLabel(id: string): string {
  return SOURCE_LABELS[id] ?? id;
}

export function sourceHint(id: string): string | undefined {
  return SOURCE_HINTS[id];
}

export function destinationLabel(id: string): string {
  return DESTINATION_LABELS[id] ?? id;
}

export function destinationHint(id: string): string | undefined {
  return DESTINATION_HINTS[id];
}

/**
 * Couleur d'une source ou d'une destination : teintes validées des statistiques (--job-1 à --job-8) dans l'ordre
 * fixe, « autres » en gris. La couleur suit la catégorie, jamais son rang ni le filtre.
 */
export function flowColor(id: string): string {
  if (id === 'other_in' || id === 'other_out') return 'var(--job-other)';
  const s = (SOURCE_ORDER as readonly string[]).indexOf(id);
  const d = (DESTINATION_ORDER as readonly string[]).indexOf(id);
  const i = s >= 0 ? s : d;
  return i >= 0 && i < 8 ? `var(--job-${i + 1})` : 'var(--job-other)';
}

/** Opération de la liste « clear » : retrait sans motif, motif inconnu, solde remis. */
export function clearLabel(category: string): string {
  if (category === 'subtracted') return 'Retrait sans motif';
  if (category === 'set') return 'Solde remis à une valeur';
  return 'Retrait au motif inconnu';
}

/** Part d'un total, en pour cent : « 43 % », « 2,5 % », « 0 % ». */
export function percentOf(value: number, total: number): string {
  if (total <= 0 || value <= 0) return '0 %';
  const share = (value / total) * 100;
  return `${share.toLocaleString('fr-FR', { maximumFractionDigits: share < 10 ? 1 : 0 })} %`;
}

/**
 * Jusqu'à StorePointApi 1.0.3 (27/10/2026), un retrait que le solde ne couvrait pas était noté en entier alors que le
 * solde tombait seulement à 0 : la période choisie remonte-t-elle avant le 28/10/2026 ?
 */
export function reachesBeforeDebitFix(days: number, today: Date = new Date()): boolean {
  const first = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  return first < new Date(2026, 9, 28);
}
